/**
 * Settings · Stores Configuration
 *
 * Currently exposes the per-GRN value cap for non-PO (petty) receipts.
 * Stored under settings key `non_po_grn_threshold` (default ₹5,000) and read
 * by the GRN form (`CreateGRNModal`) + enforced by the backend.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, Warehouse } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';

export default function StoresConfig() {
  const [threshold, setThreshold] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/settings/stores-config')
      .then((d) => setThreshold(String(d?.non_po_grn_threshold ?? 5000)))
      .catch(() => toast.error('Failed to load configuration'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    const value = Number(threshold);
    if (!Number.isFinite(value) || value < 0) {
      toast.error('Enter a valid amount (₹0 or more)');
      return;
    }
    setSaving(true);
    try {
      const d = await apiPut('/settings/stores-config', { non_po_grn_threshold: value });
      setThreshold(String(d?.non_po_grn_threshold ?? value));
      toast.success('Stores configuration saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="stores-config-page">
      <PageHeader title="Stores Configuration" subtitle="Limits and defaults for the Store & Inventory module." icon={Warehouse} />
      <div className="max-w-xl mt-6 bg-white border rounded-lg p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Non-PO GRN value limit (₹)</label>
          <p className="text-xs text-gray-500 mb-2">
            The maximum total value allowed for a single non-PO (petty) GRN — for small
            purchases like nails or consumables. Anything above this must go through a
            Purchase Order.
          </p>
          <input
            data-testid="non-po-threshold-input"
            type="number"
            min="0"
            value={loading ? '' : threshold}
            disabled={loading}
            onChange={(e) => setThreshold(e.target.value)}
            className="w-48 border rounded-lg px-3 py-2 text-sm"
            placeholder={loading ? 'Loading…' : '5000'}
          />
        </div>
        <button
          data-testid="stores-config-save-btn"
          onClick={save}
          disabled={saving || loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#1F7A6E] text-white rounded-lg disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
}
